/**
 * K1 „Dieses Jahr gegen die Vorjahre" (P2-V3 § 2.2): 01.01. bis zum PL-X-Datenstand je Jahr, Vorjahre bis zum selben Tag
 * und zur selben Uhrzeit. Erwarteter Wert (Summe · Ø), Veränderung „Anfos × Ø je Anfo", Bezahlquote mit ▲/▼ gegen das
 * Vorjahr (Zwei-Anteile-Test + Holm). Netto.
 * Persona-Umbau C2 (28.10.2026): Hauptdarstellung sind die Bestellquartale als Säulen um 0 — je Quartal die
 * Veränderung gegen das Vorjahresquartal, das laufende blass mit Stand —, darüber der Befund. Quartale aus derselben
 * Backend-Funktion wie die Veränderung in „Erwartung gegen Ist" (K4a, `quartale_verlauf`). Veränderungen gegen eine
 * Basis unter einem Drittel der Anfos (Jahreszeile 2025: „+465 %") stehen nicht als Zahl (`basisZuKlein`).
 * Audit D7 (30.10.2026): statt Prozent-Säulen um 0 absolute Säulenpaare (Vorjahr hell, dieses Jahr dunkel), Veränderung als
 * Beschriftung, das laufende Quartal `UNSICHER`; Höhen aus `saeulenWerte` (dieselbe Größe wie `veraenderungAus`).
 */
import { FC, useMemo } from 'react';
import { Box, Link, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import { Link as RouterLink } from 'react-router-dom';
import ChartCard from '../../../components/auswertungen/ChartCard';
import InfoTip from '../../../components/InfoTip';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { Legende, TipRow, unsicherFuellung, unsicherKonturen, useVizStyle } from '../../../components/auswertungen/vizStyle';
import {
  AnfosZelle,
  BefundZeile,
  BezahltBuendel,
  formatDelta,
  Hinweis,
  JungEtikett,
  int,
  Laden,
  nachGruppenReihenfolge,
  spanne,
  tEuro,
  useLaden,
  vorlaeufigSatz,
} from '../../../components/auswertungen/kennzahlTeile';
import { formatEur, formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { formatPeriode, KennzahlenZeile } from '../../../helpers/auswertungenData';
import { fetchKennzahlenGeteilt, QuartalVerlauf } from '../../../helpers/umsatzData';
import { basisZuKlein, bisTag, delta, quartalBefund } from './befund';
import { DatenstandText, Kartenkopf, letzterTag, P2_GRUPPEN, QuoteZelle, schwellenAus, summeGegenSumme, VeraenderungZelle, zeitraumText } from './teile';

/** Reife Spanne der Jahreszeile für den Tooltip der Quote („Anfos 01.01.–31.08.2025, gegen dieselbe Spanne 2024"). */
const reifSpanneText = (r: KennzahlenZeile): string => {
  if (!r.reif_bis || r.reif_bis <= r.von) return 'Noch keine Anfo dieses Jahres ist reif.';
  return `Anfos ${spanne(r.von.slice(0, 10), letzterTag(r.reif_bis))}, gegen dieselbe Spanne ${r.jahr - 1}.`;
};

/** Quote 2024 trägt den Hinweis „anderes Preismodell" (1. Hj. 2024, feste Tatsache, keine sich ändernde Zahl). */
const ANDERES_PREISMODELL_JAHR = 2024;

const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['jahr', 'anfos', 'wert', 'veraenderung', 'quote'],
  mittel: ['jahr', 'anfos', 'wert', 'veraenderung', 'quote'],
  schmal: ['jahr', 'wert', 'quote'],
};

/** „01.01.–22.10.2026, 22:20 gegen dieselbe Spanne 2025. … Summe gegen Summe ergäbe +30,0 %." — aus der API. */
const veraenderungZusatz = (r: KennzahlenZeile, vorjahr: KennzahlenZeile | undefined): string => {
  if (!r.veraenderung) return '';
  const b = r.veraenderung.basis;
  const teile = [`${zeitraumText(r.von, r.bis)}, ${r.bis.slice(11, 16)} gegen dieselbe Spanne ${r.jahr - 1}.`];
  if (b.without_value > 0) teile.push(`${int(b.without_value)} Anfos von ${r.jahr - 1} haben keinen Erwarteten Wert; sie zählen als Anfo, nicht als 0 €.`);
  const sgs = vorjahr ? summeGegenSumme(r.value_sum ?? 0, vorjahr.value_sum ?? 0) : null;
  if (sgs !== null) teile.push(`Summe gegen Summe ergäbe ${formatDelta(sgs, 1)}.`);
  return teile.join(' ');
};

/** Veränderung einer Jahreszeile; gegen eine zu kleine Basis „—" mit dem rechnerischen Wert im Tooltip (C2). */
const VeraenderungJahr: FC<{ r: KennzahlenZeile; zerlegung: boolean; tipZusatz: string }> = ({ r, zerlegung, tipZusatz }) => {
  const v = r.veraenderung;
  if (v && basisZuKlein(r.anfos - r.provisional, v.basis.anfos))
    return (
      <Tooltip
        title={`Basis zu klein: ${r.jahr - 1} hat in derselben Spanne nur ${int(v.basis.anfos)} Anfos, ${r.jahr} ${int(r.anfos - r.provisional)}. Rechnerisch ${formatDelta(v.delta, 0)} — das misst vor allem die kleine Basis, nicht dieses Jahr. Veränderungen gegen eine Basis unter einem Drittel der Anfos stehen deshalb nicht als Zahl.`}
        arrow
        placement="top"
      >
        <Box component="span" sx={{ cursor: 'help', color: 'text.secondary', lineHeight: 1.25 }}>
          —
          <Box component="span" sx={{ display: 'block', fontSize: 11 }}>
            Basis zu klein
          </Box>
        </Box>
      </Tooltip>
    );
  return <VeraenderungZelle v={v} zerlegung={zerlegung} ohneGrund={`Für ${r.jahr - 1} liegen keine Anfos im Datenbestand.`} tipZusatz={tipZusatz} />;
};

/**
 * Säulenhöhen eines Quartals (Audit D7, Codex-Prüfung 3): Anfos × Ø Erwarteter Wert je BEWERTETER Anfo — genau die Größe,
 * aus der `veraenderungAus` das Delta rechnet (hochgerechnet auf alle Anfos, keine rohe Wertsumme). Vorjahr aus der Basis;
 * dieses Jahr als Vorjahr × (1 + Δ), damit die beiden Säulen und die Beschriftung nie auseinanderlaufen. `ohneWert`:
 * Anfos ohne Erwarteten Wert (jetzt aus Wertsumme ÷ Ø zurückgerechnet — die API liefert die Zahl je Quartal nicht direkt).
 */
export function saeulenWerte(q: Pick<QuartalVerlauf, 'anfos' | 'value_sum' | 'veraenderung'>): {
  vorjahr: number | null;
  jetzt: number | null;
  ohneWertJetzt: number | null;
  ohneWertVorjahr: number | null;
} {
  const v = q.veraenderung;
  if (!v) return { vorjahr: null, jetzt: null, ohneWertJetzt: null, ohneWertVorjahr: null };
  const b = v.basis;
  const oBasis = b.with_value > 0 ? b.value_sum / b.with_value : null;
  const vorjahr = oBasis === null ? null : b.anfos * oBasis;
  const jetzt = vorjahr === null || v.delta === null ? null : vorjahr * (1 + v.delta / 100);
  const oJetzt = oBasis === null || v.delta_je_anfo === null ? null : oBasis * (1 + v.delta_je_anfo / 100);
  const ohneWertJetzt = oJetzt && oJetzt > 0 ? Math.max(0, q.anfos - Math.round(q.value_sum / oJetzt)) : null;
  return { vorjahr, jetzt, ohneWertJetzt, ohneWertVorjahr: b.without_value };
}

/** „120 T€" an der Achse (ganze T€, wie die Tabelle). */
const achseTEuro = (v: string | number | Date) => `${(Number(v) / 1000).toLocaleString('de-DE', { maximumFractionDigits: 0 })} T€`;

type Saeule = {
  quarter: string;
  vorjahr: number;
  jetzt: number;
  echt: number | null;
  vergleichbar: boolean;
  laufend: boolean;
  ohneWertJetzt: number | null;
  ohneWertVorjahr: number | null;
  q: QuartalVerlauf;
};

/**
 * C2/D7: je Bestellquartal ein Säulenpaar — Vorjahresquartal (hell) und dieses Jahr (dunkel, gleiche Farbe), absolut in
 * T€; die Veränderung steht als Beschriftung über dem Paar. Das laufende Quartal trägt `UNSICHER` (Deckkraft + Kontur).
 */
const QuartalsGrafik: FC<{ qs: QuartalVerlauf[] }> = ({ qs }) => {
  const viz = useVizStyle();
  const theme = useTheme();
  const breit = useMediaQuery(theme.breakpoints.up('sm'));
  const { daten, vorn } = useMemo(() => {
    const alle: Saeule[] = qs
      .filter((q) => q.veraenderung && q.veraenderung.delta !== null)
      .map((q) => {
        const v = q.veraenderung!;
        const w = saeulenWerte(q);
        return {
          quarter: q.quarter,
          vorjahr: w.vorjahr ?? 0,
          jetzt: w.jetzt ?? 0,
          echt: v.delta,
          vergleichbar: !basisZuKlein(q.anfos, v.basis.anfos),
          laufend: q.laufend,
          ohneWertJetzt: w.ohneWertJetzt,
          ohneWertVorjahr: w.ohneWertVorjahr,
          q,
        };
      });
    // Führende Quartale mit zu kleiner Basis fallen weg (stehen im Satz darüber); mindestens die letzten vier bleiben.
    // Am Handy höchstens vier Paare.
    const erstes = alle.findIndex((d) => d.vergleichbar);
    const ab = Math.min(erstes < 0 ? alle.length : erstes, Math.max(0, alle.length - 4));
    const gezeigt = breit ? alle.slice(ab) : alle.slice(-4);
    return { daten: gezeigt, vorn: alle.slice(0, alle.length - gezeigt.length).filter((d) => !d.vergleichbar) };
  }, [qs, breit]);
  if (daten.length === 0) return null;
  const hoechst = Math.max(1, ...daten.flatMap((d) => [d.vorjahr, d.jetzt]));
  const laufend = daten.find((d) => d.laufend);
  const farbeJetzt = (d: Saeule) => (d.laufend ? unsicherFuellung(viz.series1) : viz.series1);
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>
        {TERMS.wertBeiBestellung.short} je Bestellquartal (Anfos × Ø je Anfo) gegen dasselbe Quartal ein Jahr früher, darüber die Veränderung
        {laufend ? ` · ${formatPeriode(laufend.quarter)} bis ${bisTag(laufend.q.fenster.bis)}, Vorjahr bis zum selben Tag` : ''}
        {vorn.length > 0 &&
          ` · ${formatPeriode(vorn[0].quarter)}${vorn.length > 1 ? ` – ${formatPeriode(vorn[vorn.length - 1].quarter)}` : ''} ohne Säulen: Basis zu klein (Vorjahresquartal mit weniger als einem Drittel der Anfos)`}
      </Box>
      <Legende
        items={[
          { label: 'Vorjahresquartal', color: viz.series1Soft },
          { label: 'dieses Jahr', color: viz.series1 },
          ...(laufend ? [{ label: `${formatPeriode(laufend.quarter)} läuft noch`, color: viz.series1, unsicher: true }] : []),
        ]}
      />
      <Box sx={{ height: 240 }} data-grafik="k1-saeulenpaare">
        <ResponsiveBar
          data={daten as unknown as BarDatum[]}
          keys={['vorjahr', 'jetzt']}
          indexBy="quarter"
          groupMode="grouped"
          margin={{ top: 22, right: 8, bottom: 32, left: 52 }}
          padding={breit ? 0.35 : 0.25}
          innerPadding={2}
          valueScale={{ type: 'linear', min: 0, max: hoechst * 1.12 }}
          colors={(b) => (b.id === 'vorjahr' ? viz.series1Soft : farbeJetzt(b.data as unknown as Saeule))}
          borderRadius={2}
          theme={viz.nivo}
          animate={false}
          enableLabel={false}
          enableGridY
          gridYValues={4}
          axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: 4, format: achseTEuro }}
          axisBottom={{ tickSize: 0, tickPadding: 6, format: (v) => formatPeriode(String(v)) }}
          tooltip={({ data: d }) => {
            const x = d as unknown as Saeule;
            const v = x.q.veraenderung!;
            const ohne = [x.ohneWertJetzt, x.ohneWertVorjahr].map((n) => n ?? 0);
            return (
              <Box sx={{ ...(viz.nivo.tooltip as { container: object }).container, px: 1.5, py: 1, maxWidth: 320, whiteSpace: 'normal' }}>
                <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                  {formatPeriode(x.quarter)}
                  {x.laufend ? ` bis ${bisTag(x.q.fenster.bis)} (läuft)` : ''}
                </Box>
                <TipRow color={viz.series1} label="dieses Jahr" value={tEuro(x.jetzt)} />
                <TipRow color={viz.series1Soft} label="Vorjahresquartal" value={tEuro(x.vorjahr)} />
                <TipRow label="Veränderung" value={x.vergleichbar ? delta(x.echt) : 'Basis zu klein'} />
                <Box sx={{ opacity: 0.85 }}>
                  {TERMS.anfos.label} {int(x.q.anfos)} gegen {int(v.basis.anfos)} ({delta(v.delta_anfos)}) · Ø je Anfo {delta(v.delta_je_anfo)}
                </Box>
                <Box sx={{ opacity: 0.85 }}>
                  Anfos × Ø Erwarteter Wert je bewerteter Anfo, hochgerechnet auf alle Anfos ({int(ohne[0])} bzw. {int(ohne[1])} im Vorjahr ohne Wert)
                </Box>
                {!x.vergleichbar && <Box sx={{ opacity: 0.85 }}>Rechnerisch {delta(x.echt)} — die Basis hat weniger als ein Drittel der Anfos.</Box>}
              </Box>
            );
          }}
          layers={[
            'grid',
            'axes',
            'bars',
            unsicherKonturen((b) => (b.data.id === 'jetzt' && (b.data.data as unknown as Saeule).laufend ? viz.series1 : null)),
            ({ bars }) => {
              // Veränderung über dem Paar: mittig über beiden Säulen, über der höheren.
              const paare = new Map<string, { x0: number; x1: number; y: number; s: Saeule }>();
              for (const b of bars) {
                const s = b.data.data as unknown as Saeule;
                const p = paare.get(s.quarter);
                if (!p) paare.set(s.quarter, { x0: b.x, x1: b.x + b.width, y: b.y, s });
                else paare.set(s.quarter, { x0: Math.min(p.x0, b.x), x1: Math.max(p.x1, b.x + b.width), y: Math.min(p.y, b.y), s });
              }
              return (
                <g>
                  {Array.from(paare.values()).map(({ x0, x1, y, s }) => (
                    <text
                      key={`l-${s.quarter}`}
                      x={(x0 + x1) / 2}
                      y={y - 6}
                      textAnchor="middle"
                      fontSize={breit ? 12 : 11}
                      fontFamily={viz.fontFamily}
                      fontWeight={s.vergleichbar ? 600 : 400}
                      fill={s.vergleichbar ? viz.inkPrimary : viz.inkSecondary}
                    >
                      {s.vergleichbar ? delta(s.echt) : breit ? 'Basis zu klein' : '—'}
                    </text>
                  ))}
                </g>
              );
            },
          ]}
          role="img"
          ariaLabel="Erwarteter Wert je Bestellquartal gegen das Vorjahresquartal"
        />
      </Box>
    </Box>
  );
};

const KennzahlenKarte: FC = () => {
  const { data, fehler } = useLaden(fetchKennzahlenGeteilt);
  const s = schwellenAus(data);
  const rows = data?.rows ?? [];
  const vorjahrVon = (r: KennzahlenZeile) => rows.find((x) => x.jahr === r.jahr - 1);

  const baue = (sichtbar: ReadonlySet<string>, schmal: boolean): Spalte<KennzahlenZeile>[] => [
    {
      key: 'jahr',
      label: 'Jahr',
      gruppe: 'Menge',
      pflicht: true,
      render: (r) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {r.jahr}
          </Box>
          <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>
            {zeitraumText(r.von, r.bis).replace(/\d{4}$/, '')}
          </Box>
          {!sichtbar.has('anfos') && (
            <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>
              {int(r.anfos)} Anfos
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: 'anfos',
      label: TERMS.anfos.label,
      tip: TERMS.anfos.tip,
      gruppe: 'Menge',
      align: 'right',
      render: (r) => <AnfosZelle n={r.anfos} vorlaeufig={r.provisional} />,
    },
    {
      key: 'wert',
      label: TERMS.wertBeiBestellung.short,
      tip: TERMS.wertBeiBestellung.tip,
      einheit: '36 Monate · Summe · Ø je Anfo',
      gruppe: 'Wert',
      pflicht: true,
      align: 'right',
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {tEuro(r.value_sum)}
          </Box>
          <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
            Ø {formatEur(r.value_per_anfo ?? null, 0)}
          </Box>
          {schmal && !sichtbar.has('veraenderung') && (
            <Box component="span" sx={{ display: 'block', fontSize: 11 }}>
              <VeraenderungJahr r={r} zerlegung={false} tipZusatz={veraenderungZusatz(r, vorjahrVon(r))} />
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: 'veraenderung',
      label: TERMS.veraenderungVorjahreszeitraum.label,
      tip: TERMS.veraenderungVorjahreszeitraum.tip,
      einheit: TERMS.veraenderungVorjahreszeitraum.einheit,
      gruppe: 'Trend',
      align: 'right',
      render: (r) => (
        <VeraenderungJahr r={r} zerlegung={!schmal} tipZusatz={veraenderungZusatz(r, vorjahrVon(r))} />
      ),
    },
    {
      key: 'quote',
      label: TERMS.bezahlquote.label,
      tip: TERMS.bezahlquote.tip,
      einheit: `Anfos ab ${s.reife_tage} Tagen`,
      gruppe: 'Zahlung',
      pflicht: true,
      align: 'right',
      unsicher: (r) => (r.is_young ? 'jung' : null),
      render: (r) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'flex-start' }}>
          <QuoteZelle rate={r.pay_rate} bezahlt={r.mature_paid} reif={r.mature_anfos} jung={r.is_young} test={r.pay_rate_test} tipZusatz={reifSpanneText(r)} />
          {r.jahr === ANDERES_PREISMODELL_JAHR && <InfoTip text={TERMS.anderesPreismodell.tip} />}
        </Box>
      ),
    },
    {
      key: 'jeAnfo',
      label: TERMS.bezahltJeAnfo.label,
      tip: TERMS.bezahltJeAnfo.tip,
      einheit: 'bezahlte Anfos · Ø Alter',
      gruppe: 'Zahlung',
      align: 'right',
      render: (r) => <BezahltBuendel wert={formatEur(r.paid_per_anfo, 0)} zahler={r.payers ?? 0} alterTage={r.avg_age_days === null ? null : Math.round(r.avg_age_days)} />,
    },
    {
      key: 'bezahlt',
      label: `${TERMS.umsatzNetto.label} (Summe)`,
      tip: TERMS.umsatzNetto.tip,
      einheit: 'bezahlte Anfos · Ø Alter',
      gruppe: 'Zahlung',
      align: 'right',
      render: (r) => <BezahltBuendel wert={tEuro(r.paid_revenue)} zahler={r.payers ?? 0} alterTage={r.avg_age_days === null ? null : Math.round(r.avg_age_days)} />,
    },
    {
      key: 'erst',
      label: TERMS.anteilErstbesteller.label,
      tip: TERMS.anteilErstbesteller.tip,
      gruppe: 'Kundentyp',
      align: 'right',
      render: (r) => formatPct(r.first_buyer_share ?? null),
    },
    {
      key: 'hoch',
      label: TERMS.hochrechnung.label,
      tip: TERMS.hochrechnung.tip,
      einheit: 'Summe, netto',
      gruppe: 'Modell',
      align: 'right',
      render: (r) => tEuro(r.projected_value ?? null),
    },
  ];
  const meta = nachGruppenReihenfolge(spaltenMeta(baue(new Set(), false)), P2_GRUPPEN);
  const wahl = useSpaltenwahl('umsatzJahr', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);
  const schmal = wahl.breite === 'schmal';
  const spalten = baue(sichtbar, schmal).filter((c) => sichtbar.has(c.key));

  const vorlaeufig = rows.reduce((a, r) => a + r.provisional, 0);
  const qs = data?.quartale_verlauf ?? [];
  const befund = data ? quartalBefund(qs) : null;

  return (
    <ChartCard
      title="Dieses Jahr gegen die Vorjahre"
      anker="dieses-jahr-gegen-vorjahre"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead={
        <>
          Die Säulenpaare zeigen je Bestellquartal den{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {TERMS.wertBeiBestellung.short}
          </Box>{' '}
          (Anfos mal Ø je Anfo) neben demselben Quartal ein Jahr früher, die Zahl darüber die Veränderung; die Tabelle darunter je Jahr dieselbe Spanne. Der
          Erwartete Wert ist, was die Bestellungen über 36 Monate voraussichtlich einbringen, netto, bewertet mit dem heutigen Wert ihrer
          Angebote — nicht das Geld, das eingegangen ist; das zeigt die Seite{' '}
          <Link component={RouterLink} to="/auswertungen/umsatz/cashflow" underline="hover">
            Cashflow
          </Link>{' '}
          (brutto).
        </>
      }
      terms={[
        { label: TERMS.wertBeiBestellung.label, tip: TERMS.wertBeiBestellung.tip },
        { label: TERMS.veraenderungVorjahresquartal.label, tip: TERMS.veraenderungVorjahresquartal.tip },
        { label: TERMS.veraenderungVorjahreszeitraum.label, tip: TERMS.veraenderungVorjahreszeitraum.tip },
        { label: TERMS.bezahlquote.label, tip: TERMS.bezahlquote.tip },
        { label: TERMS.abweichungZeitraumMarke.label, tip: TERMS.abweichungZeitraumMarke.tip },
        { label: TERMS.jung.label, tip: TERMS.jung.tip },
        ...(vorlaeufig > 0 ? [{ label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip }] : []),
      ]}
      source="PL-X-Bestellungen und Zahlungen, Anfo-Wert je Anfo, PX-Data-Export"
      freshnessKey={['plx_orders', 'anfo_werte', 'px_ergaenzung']}
    >
      <Box ref={wahl.messRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            {befund && <BefundZeile text={'text' in befund ? befund.text : undefined} fehlt={'fehlt' in befund ? befund.fehlt : null} />}
            <QuartalsGrafik qs={qs} />
            <Kartenkopf>
              Tabelle: 1. Januar bis <DatenstandText ds={data.datenstand} />, Vorjahre bis zum selben Tag und zur selben Uhrzeit
            </Kartenkopf>
            <SortableTable
              rows={data.rows}
              columns={spalten}
              rowKey={(r) => String(r.jahr)}
              maxHeight={null}
              eng={schmal}
              namensEtikett={(r) => (r.is_young ? <JungEtikett jungAnteil={s.jung_anteil} jungTage={s.jung_tage} /> : null)}
            />
            <Hinweis>
              Alle Euro-Beträge netto (ohne MwSt.). — = nicht messbar, der Grund steht im Tooltip. Keine Säule bzw. „Basis zu klein“: das
              Vergleichsquartal oder -jahr hat weniger als ein Drittel der Anfos; die Veränderung mäße dann vor allem die kleine Basis.
              Bezahlquote nur über Anfos, die mindestens {s.reife_tage} Tage alt sind — in jedem Jahr dieselbe Spanne (1.1. bis {s.reife_tage}{' '}
              Tage vor dem Stichtag), damit kein Jahr Monate mehr hat; im Januar und Februar steht deshalb noch keine Quote. Die Quote
              ändert sich über die Zeit, ein ▼ zeigt also auch diesen Trend. ▲/▼ steht nur, wo ein Test über beide Jahre den
              Unterschied zum Vorjahr vom Zufall trennt; ohne Marke kann er Zufall sein. Wer nicht zahlt: Karte „Wer bestellt — und wer zahlt“
              (12 Monate, anderes Fenster). {vorlaeufigSatz(vorlaeufig)}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default KennzahlenKarte;
